import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import './SocialMediaPostManager.css';

interface Post {
  postId: string;
  title: string;
  content: string;
  mediaUrls: string[];
  hashtags: string[];
  mentions: string[];
  scheduledAt: string;
  timezone: string;
  status: 'draft' | 'scheduled' | 'publishing' | 'published' | 'failed';
  platforms: string[];
  platformPostIds: { [key: string]: string };
  failureReason?: string;
  isRecurring: boolean;
  campaignId?: string;
  discountId?: string;
  offerCode?: string;
  isBoosted: boolean;
  boostBudget?: number;
  boostStatus?: string;
  impressions: { [key: string]: number };
  engagements: { [key: string]: number };
  clicks: { [key: string]: number };
  conversions: number;
  revenue: number;
  createdAt: string;
  updatedAt: string;
}

interface Campaign {
  campaignId: string;
  name: string;
  status: string;
  startDate: string;
  endDate?: string;
}

interface Discount {
  discountId: string;
  name: string;
  discountType: string;
  value: number;
  isActive: boolean;
}

interface SocialMediaAccount {
  accountId: string;
  platform: string;
  accountName: string;
  isActive: boolean;
}

interface PostTemplate {
  templateId: string;
  name: string;
  titleTemplate: string;
  contentTemplate: string;
  defaultHashtags: string[];
  category: string;
}

const PLATFORM_ICONS = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'Twitter',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok'
};

const STATUS_COLORS = {
  draft: '#6c757d',
  scheduled: '#ffc107',
  publishing: '#17a2b8',
  published: '#28a745',
  failed: '#dc3545'
};

interface SocialMediaPostManagerProps {
  storeId?: string;
}

export const SocialMediaPostManager: React.FC<SocialMediaPostManagerProps> = ({ storeId: parentStoreId }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [accounts, setAccounts] = useState<SocialMediaAccount[]>([]);
  const [templates, setTemplates] = useState<PostTemplate[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>(parentStoreId || '');
  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [showPostNow, setShowPostNow] = useState(false);
  
  const [quickPost, setQuickPost] = useState({
    title: '',
    content: '',
    hashtags: '',
    platforms: [] as string[],
    mediaFiles: [] as File[],
    selectedTemplate: '',
    selectedCampaign: '',
    selectedDiscount: '',
    isBoosted: false,
    boostBudget: 0
  });

  // Sync with parent store selection
  useEffect(() => {
    if (parentStoreId && parentStoreId !== selectedStore) {
      setSelectedStore(parentStoreId);
    }
  }, [parentStoreId]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      fetchPosts();
      fetchAccounts();
    }
  }, [selectedStore]);

  const fetchInitialData = async () => {
    try {
      // Only fetch stores if no parent storeId was provided
      if (!parentStoreId) {
        const storesResponse = await authenticatedFetch(API_ENDPOINTS.stores());
        if (storesResponse.ok) {
          const storesData = await storesResponse.json();
          const storeList = Array.isArray(storesData) ? storesData : (storesData.data || storesData.stores || []);
          setStores(storeList);
          if (storeList.length > 0) {
            setSelectedStore(storeList[0].storeId);
          }
        }
      }

      // Fetch campaigns
      const campaignsResponse = await authenticatedFetch(API_ENDPOINTS.socialMediaCampaigns());
      if (campaignsResponse.ok) {
        const campaignsData = await campaignsResponse.json();
        setCampaigns(campaignsData.data || []);
      }

      // Fetch discounts (using existing discount system)
      try {
        const discountsResponse = await authenticatedFetch(API_ENDPOINTS.discountCodes());
        if (discountsResponse.ok) {
          const discountsData = await discountsResponse.json();
          setDiscounts(discountsData.data || []);
        }
      } catch (err) {
        console.log('Discount system not yet implemented');
      }

      // Fetch templates
      try {
        const templatesResponse = await authenticatedFetch(API_ENDPOINTS.socialMediaTemplates());
        if (templatesResponse.ok) {
          const templatesData = await templatesResponse.json();
          setTemplates(templatesData.data || []);
        }
      } catch (err) {
        console.log('Templates not yet available');
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    }
  };

  const fetchPosts = async () => {
    if (!selectedStore) return;

    setLoading(true);
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.socialMediaPosts()}?storeId=${selectedStore}`);
      if (!response.ok) throw new Error('Failed to fetch posts');
      const data = await response.json();
      setPosts(data.data || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    if (!selectedStore) return;

    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.socialMediaAccounts()}?storeId=${selectedStore}`);
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  const handlePostNow = async () => {
    if (!selectedStore || !quickPost.content || quickPost.platforms.length === 0) {
      setError('Please fill in all required fields and select at least one platform');
      return;
    }

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('storeId', selectedStore);
      formData.append('title', quickPost.title);
      formData.append('content', quickPost.content);
      formData.append('hashtags', JSON.stringify(quickPost.hashtags.split(',').map(tag => tag.trim()).filter(Boolean)));
      formData.append('platforms', JSON.stringify(quickPost.platforms));
      formData.append('scheduledAt', new Date().toISOString());
      formData.append('publishImmediately', 'true');

      if (quickPost.selectedCampaign) {
        formData.append('campaignId', quickPost.selectedCampaign);
      }

      if (quickPost.selectedDiscount) {
        formData.append('discountId', quickPost.selectedDiscount);
      }

      if (quickPost.isBoosted && quickPost.boostBudget > 0) {
        formData.append('isBoosted', 'true');
        formData.append('boostBudget', quickPost.boostBudget.toString());
      }

      // Add media files
      quickPost.mediaFiles.forEach((file, index) => {
        formData.append(`media_${index}`, file);
      });

      const response = await authenticatedFetch(API_ENDPOINTS.socialMediaPosts(), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Failed to publish post');
      
      // Reset form
      setQuickPost({
        title: '',
        content: '',
        hashtags: '',
        platforms: [],
        mediaFiles: [],
        selectedTemplate: '',
        selectedCampaign: '',
        selectedDiscount: '',
        isBoosted: false,
        boostBudget: 0
      });
      
      setShowPostNow(false);
      await fetchPosts();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish post');
    }
  };

  const handleRetryPost = async (postId: string) => {
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.socialMediaPost(postId)}/retry`, {
        method: 'POST'
      });

      if (!response.ok) throw new Error('Failed to retry post');
      
      await fetchPosts();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry post');
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await authenticatedFetch(API_ENDPOINTS.socialMediaPost(postId), {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete post');
      
      await fetchPosts();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete post');
    }
  };

  const handleApplyTemplate = (templateId: string) => {
    const template = templates.find(t => t.templateId === templateId);
    if (template) {
      setQuickPost({
        ...quickPost,
        title: template.titleTemplate,
        content: template.contentTemplate,
        hashtags: template.defaultHashtags.join(', '),
        selectedTemplate: templateId
      });
    }
  };

  const handleMediaFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const validFiles = files.filter(file => {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        const isValidSize = file.size <= 100 * 1024 * 1024; // 100MB limit
        return (isImage || isVideo) && isValidSize;
      });
      
      if (validFiles.length !== files.length) {
        setError('Some files were skipped. Only images and videos under 100MB are allowed.');
      }
      
      setQuickPost({
        ...quickPost,
        mediaFiles: [...quickPost.mediaFiles, ...validFiles].slice(0, 10) // Max 10 files
      });
    }
  };

  const removeMediaFile = (index: number) => {
    const newFiles = [...quickPost.mediaFiles];
    newFiles.splice(index, 1);
    setQuickPost({
      ...quickPost,
      mediaFiles: newFiles
    });
  };

  const filteredPosts = posts.filter(post => {
    const statusMatch = statusFilter === 'all' || post.status === statusFilter;
    const platformMatch = platformFilter === 'all' || (post.platforms || []).includes(platformFilter);
    return statusMatch && platformMatch;
  });

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  const getStatusBadge = (status: string) => (
    <span 
      className="status-badge" 
      style={{ backgroundColor: STATUS_COLORS[status as keyof typeof STATUS_COLORS] }}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );

  const getPlatformIcons = (platforms: string[]) => (
    <div className="platform-icons">
      {platforms.map(platform => (
        <span key={platform} title={platform}>
          {PLATFORM_ICONS[platform as keyof typeof PLATFORM_ICONS]}
        </span>
      ))}
    </div>
  );

  if (loading && !posts.length) {
    return <div className="post-manager-loading">Loading posts...</div>;
  }

  return (
    <div className="social-media-post-manager">
      <div className="manager-header">
        <h1>Social Media Posts</h1>
        
        <div className="header-actions">
          {!parentStoreId && stores.length > 0 && (
            <div className="store-selector">
              <select
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
              >
                {stores.map(store => (
                  <option key={store.storeId} value={store.storeId}>
                    {store.name} - {store.address?.city}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <button 
            className="post-now-btn"
            onClick={() => setShowPostNow(true)}
          >
            Post Now
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>[ERROR] {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="filters-section">
        <div className="filter-group">
          <label>Status:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="publishing">Publishing</option>
            <option value="published">Published</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Platform:</label>
          <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)}>
            <option value="all">All Platforms</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
            <option value="twitter">Twitter</option>
            <option value="linkedin">LinkedIn</option>
            <option value="tiktok">TikTok</option>
          </select>
        </div>
        
        <div className="results-count">
          {filteredPosts.length} post{filteredPosts.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="posts-grid">
        {filteredPosts.length === 0 ? (
          <div className="no-posts">
            <p>No posts found. Create your first post to get started!</p>
            <button onClick={() => setShowPostNow(true)}>Create Post</button>
          </div>
        ) : (
          filteredPosts.map(post => (
            <div key={post.postId} className="post-card">
              <div className="post-header">
                <div className="post-title">
                  {post.title || 'Untitled Post'}
                </div>
                <div className="post-meta">
                  {getStatusBadge(post.status)}
                  {getPlatformIcons(post.platforms || [])}
                </div>
              </div>

              <div className="post-content">
                <p>{post.content}</p>
                {(post.hashtags || []).length > 0 && (
                  <div className="post-hashtags">
                    {(post.hashtags || []).map(tag => (
                      <span key={tag} className="hashtag">#{tag}</span>
                    ))}
                  </div>
                )}
              </div>

              {(post.mediaUrls || []).length > 0 && (
                <div className="post-media">
                  {(post.mediaUrls || []).slice(0, 3).map((url, index) => (
                    <img key={index} src={url} alt={`Media ${index + 1}`} />
                  ))}
                  {(post.mediaUrls || []).length > 3 && (
                    <div className="more-media">+{(post.mediaUrls || []).length - 3}</div>
                  )}
                </div>
              )}

              <div className="post-details">
                <div className="schedule-info">
                  Scheduled: {formatDate(post.scheduledAt)}
                </div>
                {post.isBoosted && (
                  <div className="boost-info">
                    [BOOSTED] ${post.boostBudget}
                  </div>
                )}
                {post.offerCode && (
                  <div className="offer-info">
                    Offer: {post.offerCode}
                  </div>
                )}
              </div>

              {post.status === 'published' && (
                <div className="post-stats">
                  <div className="stat">
                    <span className="stat-label">Impressions</span>
                    <span className="stat-value">
                      {Object.values(post.impressions).reduce((sum, val) => sum + val, 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Engagements</span>
                    <span className="stat-value">
                      {Object.values(post.engagements).reduce((sum, val) => sum + val, 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Clicks</span>
                    <span className="stat-value">
                      {Object.values(post.clicks).reduce((sum, val) => sum + val, 0).toLocaleString()}
                    </span>
                  </div>
                  {post.revenue > 0 && (
                    <div className="stat">
                      <span className="stat-label">Revenue</span>
                      <span className="stat-value">${post.revenue.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              {post.status === 'failed' && (
                <div className="failure-info">
                  <span className="failure-reason">[FAILED] {post.failureReason}</span>
                  <button 
                    className="retry-btn"
                    onClick={() => handleRetryPost(post.postId)}
                  >
                    Retry
                  </button>
                </div>
              )}

              <div className="post-actions">
                <button 
                  className="delete-btn"
                  onClick={() => handleDeletePost(post.postId)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Quick Post Modal */}
      {showPostNow && (
        <div className="modal-overlay">
          <div className="post-now-modal">
            <div className="modal-header">
              <h2>Post Now</h2>
              <button onClick={() => setShowPostNow(false)}>×</button>
            </div>

            <div className="modal-content">
              {templates.length > 0 && (
                <div className="template-section">
                  <label>Quick Templates:</label>
                  <div className="template-buttons">
                    {templates.slice(0, 4).map(template => (
                      <button
                        key={template.templateId}
                        className="template-btn"
                        onClick={() => handleApplyTemplate(template.templateId)}
                      >
                        {template.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Title (optional)</label>
                <input
                  type="text"
                  value={quickPost.title}
                  onChange={(e) => setQuickPost({...quickPost, title: e.target.value})}
                  placeholder="Post title..."
                />
              </div>

              <div className="form-group">
                <label>Content *</label>
                <textarea
                  value={quickPost.content}
                  onChange={(e) => setQuickPost({...quickPost, content: e.target.value})}
                  placeholder="What's happening?"
                  rows={4}
                  required
                />
              </div>

              <div className="form-group">
                <label>Hashtags</label>
                <input
                  type="text"
                  value={quickPost.hashtags}
                  onChange={(e) => setQuickPost({...quickPost, hashtags: e.target.value})}
                  placeholder="#restaurant, #food, #special"
                />
              </div>

              <div className="form-group">
                <label>Platforms *</label>
                <div className="platform-selection">
                  {accounts.filter(acc => acc.isActive).map(account => (
                    <label key={account.accountId} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={quickPost.platforms.includes(account.platform)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setQuickPost({
                              ...quickPost, 
                              platforms: [...quickPost.platforms, account.platform]
                            });
                          } else {
                            setQuickPost({
                              ...quickPost, 
                              platforms: quickPost.platforms.filter(p => p !== account.platform)
                            });
                          }
                        }}
                      />
                      <span>
                        {PLATFORM_ICONS[account.platform as keyof typeof PLATFORM_ICONS]}: {account.accountName}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Media Files</label>
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleMediaFileChange}
                />
                {quickPost.mediaFiles.length > 0 && (
                  <div className="selected-media">
                    {quickPost.mediaFiles.map((file, index) => (
                      <div key={index} className="media-item">
                        <span>{file.name}</span>
                        <button onClick={() => removeMediaFile(index)}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {campaigns.length > 0 && (
                <div className="form-group">
                  <label>Campaign (optional)</label>
                  <select
                    value={quickPost.selectedCampaign}
                    onChange={(e) => setQuickPost({...quickPost, selectedCampaign: e.target.value})}
                  >
                    <option value="">No campaign</option>
                    {campaigns.filter(c => c.status === 'active').map(campaign => (
                      <option key={campaign.campaignId} value={campaign.campaignId}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {discounts.length > 0 && (
                <div className="form-group">
                  <label>Include Discount (optional)</label>
                  <select
                    value={quickPost.selectedDiscount}
                    onChange={(e) => setQuickPost({...quickPost, selectedDiscount: e.target.value})}
                  >
                    <option value="">No discount</option>
                    {discounts.filter(d => d.isActive).map(discount => (
                      <option key={discount.discountId} value={discount.discountId}>
                        {discount.name} - {discount.value}% off
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={quickPost.isBoosted}
                    onChange={(e) => setQuickPost({...quickPost, isBoosted: e.target.checked})}
                  />
                  Boost this post
                </label>
              </div>

              {quickPost.isBoosted && (
                <div className="form-group">
                  <label>Boost Budget ($)</label>
                  <input
                    type="number"
                    min={5}
                    step={5}
                    value={quickPost.boostBudget}
                    onChange={(e) => setQuickPost({...quickPost, boostBudget: parseFloat(e.target.value) || 0})}
                    placeholder="50"
                  />
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowPostNow(false)}>Cancel</button>
              <button 
                onClick={handlePostNow}
                className="post-btn"
                disabled={!quickPost.content || quickPost.platforms.length === 0}
              >
                Post Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};